import { action, computed, IObservableArray, observable } from 'mobx';
import equals from 'ramda/src/equals';
import without from 'ramda/src/without';
import { BaseStore } from '~web/store/base/base-store';
import { TokenInfo } from '~web/view/model/token-info';

const TOKENS_TO_SCROLL = 4;

// TODO(DMITRY): Merge this store with TokenAssignmentFlowStore or make it dependent on it
export class TokenSelectionStore extends BaseStore {
  @observable tokenId: string;
  @observable selectedTokenIds: IObservableArray<string>;
  @observable savedSelectedTokenIds: IObservableArray<string>;
  @observable tokenSelectorOverlayShown: boolean;
  @observable tokenIndex: number;
  initialTokenIds: string[];
  onSelect: (tokenId: string) => void;
  onDeselect: (tokenId: string) => void;

  constructor() {
    super();
    this.clear();
  }

  initialize(tokenId: string, initialTokenIds?: string[]): void {
    this.clear();
    this.setTokenId(tokenId);
    this.addSelectedTokenId(tokenId);
    this.onSelect = this.addSelectedTokenId;
    this.onDeselect = this.removeSelectedTokenId;
    if (initialTokenIds) {
      this.initialTokenIds = initialTokenIds;
      this.selectedTokenIds = observable.array(initialTokenIds);
    }
    this.showTokenSelectorOverlay();
  }

  @computed
  get isTokensEdited(): boolean {
    const initialTokenIds = this.initialTokenIds.length
      ? this.initialTokenIds.slice().sort()
      : null;
    const selectedTokenIds = this.selectedTokenIds.length
      ? this.selectedTokenIds.slice().sort()
      : null;
    return !equals(initialTokenIds, selectedTokenIds);
  }

  @action
  restoreSelectedTokenIds = (): void => {
    this.selectedTokenIds = this.savedSelectedTokenIds;
  };

  @action
  showTokenSelectorOverlay = (): void => {
    this.tokenSelectorOverlayShown = true;
    this.savedSelectedTokenIds = this.selectedTokenIds;
  };

  @action
  hideTokenSelectorOverlay = (): void => {
    this.tokenSelectorOverlayShown = false;
  };

  @action
  setTokenId(tokenId: string): void {
    this.tokenId = tokenId;
  }

  @action
  addSelectedTokenId = (tokenId: string): void => {
    if (!this.selectedTokenIds.includes(tokenId)) {
      this.selectedTokenIds = observable.array(this.selectedTokenIds.concat(tokenId));
    }
  };

  @action
  removeSelectedTokenId = (tokenId: string): void => {
    // cannot remove the last token - at least one token must be selected
    if (this.selectedTokenIds.length > 1 && this.selectedTokenIds.includes(tokenId)) {
      this.selectedTokenIds = observable.array(without([tokenId], this.selectedTokenIds));
    }
  };

  @action
  resetSelectedTokenIds = (): void => {
    this.selectedTokenIds = observable.array(
      this.selectedTokenIds.filter((tokenId) => tokenId === this.tokenId),
    );
  };

  @action
  setTokenIndex = (index: number): void => {
    this.tokenIndex = index;
  };

  @action
  increaseTokenIndex(): void {
    this.tokenIndex += TOKENS_TO_SCROLL;
  }

  @action
  decreaseTokenIndex(): void {
    this.tokenIndex -= TOKENS_TO_SCROLL;
  }

  setOnSelect = (selectFunction: (tokenId: string) => void): void => {
    this.onSelect = selectFunction;
  };

  setOnDeselect = (deselectFunction: (tokenId: string) => void): void => {
    this.onDeselect = deselectFunction;
  };

  /**
   * Checks if a given token is selected
   * @param token
   */
  isTokenSelected = (token: TokenInfo): boolean => this.selectedTokenIds.includes(token.tokenId);

  clear(): void {
    this.tokenId = undefined;
    this.initialTokenIds = [];
    if (this.selectedTokenIds) {
      this.selectedTokenIds.clear();
    } else {
      this.selectedTokenIds = observable.array();
    }
    this.savedSelectedTokenIds = observable.array();
    this.tokenIndex = 0;
    this.tokenSelectorOverlayShown = false;
    this.onSelect = undefined;
    this.onDeselect = undefined;
  }
}



// WEBPACK FOOTER //
// /codebuild/output/src332225586/src/modules/web/src/js/store/token/token-selection-store.ts